import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMeetingList } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import {
    AppScreen, BadgeTone, Button, CardGrid, ChipBar, ChipOption,
    MeetingCard, TimeBadge,
} from 'components/ds';
import { IcPlus } from 'components/common/icons/EmojiIcons';
import styles from './MeetingsList.module.css';

interface MeetingItem {
    id: string;
    title: string;
    host: string;
    status: string;
    time: string;
    date: string;
    location: string;
    dDay?: string;
    isRegularMeeting: boolean;
    scheduleStartDateTime?: string;
    memberCount: number;
    maxMembers: number;
    meetingType: string;
    totalPaymentAmount: number;
    myUnsettledAmount: number;
    hasActiveProposal: boolean;
    proposalVoteCount: number;
    hotelName: string;
    hotelAddress: string;
}

type TabKey = 'today' | 'upcoming' | 'regular' | 'completed';

export const MeetingsList: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const VALID_TABS: TabKey[] = ['today', 'upcoming', 'regular', 'completed'];
    const rawFilter = searchParams.get('filter');
    // 'past' 같은 구 파라미터는 'completed'로 매핑
    const normalized = rawFilter === 'past' ? 'completed' : rawFilter;
    const initialTab: TabKey = VALID_TABS.includes(normalized as TabKey) ? (normalized as TabKey) : 'upcoming';
    const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
    const { data } = useMeetingList();

    useEffect(() => { meetingStore.onGetMeetingList(); }, []);

    const now = new Date();
    const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
    const tomorrowStart = new Date(todayStart); tomorrowStart.setDate(tomorrowStart.getDate() + 1);

    const allMeetings: MeetingItem[] = data?.data?.map((m: any) => {
        const s = m.schedule || m.confirmedSchedule || (m.startDatetime ? { startDatetime: m.startDatetime, endDatetime: m.endDatetime } : null);
        const calcStatus = () => { if (!s) return 'upcoming'; const e = new Date(s.endDatetime); if (now > e) return 'ended'; const st = new Date(s.startDatetime); if (now >= st) return 'ongoing'; return 'upcoming'; };
        const calcDDay = () => { if (!s) return undefined; const td = new Date(); td.setHours(0,0,0,0); const md = new Date(s.startDatetime); md.setHours(0,0,0,0); const d = Math.round((md.getTime()-td.getTime())/86400000); return d>0?`D-${d}`:d===0?'D-Day':`D+${Math.abs(d)}`; };
        const place = m.place;
        return {
            id: m.meetingId, title: m.meetingName,
            host: m.creatorNickname ? decodeURIComponent(m.creatorNickname) : '',
            status: calcStatus(),
            time: s ? new Date(s.startDatetime).toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'}) : '',
            date: s ? new Date(s.startDatetime).toLocaleDateString('ko-KR',{month:'short',day:'numeric',weekday:'short'}) : '',
            location: place?.placeAddress || place?.placeName || '',
            dDay: calcDDay(), isRegularMeeting: m.isRegularMeeting||false,
            scheduleStartDateTime: s?.startDatetime,
            memberCount: m.currentMemberCount||m.memberCount||1, maxMembers: m.maxMembers||10,
            meetingType: m.meetingType||'DAY',
            totalPaymentAmount: m.totalPaymentAmount||0, myUnsettledAmount: m.myUnsettledAmount||0,
            hasActiveProposal: m.hasActiveProposal||false, proposalVoteCount: m.proposalVoteCount||0,
            hotelName: m.hotelName||'', hotelAddress: m.hotelAddress||'',
        };
    }) || [];

    const todayMeetings = allMeetings.filter(m => {
        if (!m.scheduleStartDateTime) return false;
        const sd = new Date(m.scheduleStartDateTime); sd.setHours(0,0,0,0);
        return sd.getTime() === todayStart.getTime();
    });
    const upcomingMeetings = allMeetings.filter(m => {
        if (m.status === 'ended') return false;
        if (!m.scheduleStartDateTime) return true; // 일정 미정도 예정에 포함
        const sd = new Date(m.scheduleStartDateTime); sd.setHours(0,0,0,0);
        return sd >= tomorrowStart;
    }).sort((a,b) => { if (!a.scheduleStartDateTime) return 1; if (!b.scheduleStartDateTime) return -1; return new Date(a.scheduleStartDateTime).getTime()-new Date(b.scheduleStartDateTime).getTime(); });
    const regularMeetings = allMeetings.filter(m => m.isRegularMeeting && m.status!=='ended');
    const completedMeetings = allMeetings.filter(m => m.status==='ended');

    const tabData: Record<TabKey,MeetingItem[]> = { today: todayMeetings, upcoming: upcomingMeetings, regular: regularMeetings, completed: completedMeetings };
    const tabs: {key:TabKey;labelKey:string}[] = [
        {key:'today',labelKey:'meetingsList.tabToday'},
        {key:'upcoming',labelKey:'meetingsList.tabUpcoming'},
        {key:'regular',labelKey:'meetingsList.tabRegular'},
        {key:'completed',labelKey:'meetingsList.tabCompleted'},
    ];
    const list = tabData[activeTab];

    const tabOptions: ChipOption[] = tabs.map(tab => ({
        value: tab.key,
        label: tabData[tab.key].length > 0
            ? `${t(tab.labelKey)} ${tabData[tab.key].length}`
            : t(tab.labelKey),
    }));

    const badgesOf = (m: MeetingItem): { label: string; tone: BadgeTone }[] => {
        const out: { label: string; tone: BadgeTone }[] = [];
        if (m.meetingType === 'TRAVEL') out.push({ label: t('home.travelTag'), tone: 'lime' });
        if (m.isRegularMeeting) out.push({ label: t('home.regularTag'), tone: 'neutral' });
        if (m.hasActiveProposal) out.push({ label: t('meetingsList.voting'), tone: 'brand' });
        if ((m.totalPaymentAmount || 0) > 0) {
            out.push({
                label: t('meetingsList.amountWon', { amount: (m.totalPaymentAmount || 0).toLocaleString() }),
                tone: 'outline',
            });
        }
        if ((m.myUnsettledAmount || 0) > 0) {
            out.push({
                label: t('meetingsList.myAmount', { amount: (m.myUnsettledAmount || 0).toLocaleString() }),
                tone: 'danger',
            });
        }
        return out;
    };

    const emptyTitle = activeTab === 'today' ? t('meetingsList.emptyToday')
        : activeTab === 'upcoming' ? t('meetingsList.emptyUpcoming')
            : activeTab === 'regular' ? t('meetingsList.emptyRegular')
                : t('meetingsList.emptyCompleted');

    return (
        <AppScreen
            title={t('meetingsList.title')}
            bar="sub"
            gnb
            onBack={() => navigate('/home')}
            floating={
                <button
                    type="button"
                    className={styles.fab}
                    onClick={() => navigate('/meeting/create/step1')}
                    aria-label={t('meetingsList.createBtn')}
                >
                    <IcPlus size={18} />
                    <span>{t('meeting.create')}</span>
                </button>
            }
        >
            <div className={styles.filter}>
                <ChipBar
                    options={tabOptions}
                    value={activeTab}
                    onChange={(v) => setActiveTab(v as TabKey)}
                    ariaLabel={t('meetingsList.title')}
                />
            </div>

            {list.length === 0 ? (
                <div className={styles.empty}>
                    <span className={styles.emptyTitle}>{emptyTitle}</span>
                    <span className={styles.emptyDesc}>
                        {activeTab === 'completed' ? t('meetingsList.emptyCompletedDesc') : t('meetingsList.emptyDesc')}
                    </span>
                    {activeTab !== 'completed' && (
                        <div className={styles.emptyAction}>
                            <Button variant="primary" size="lg" block onClick={() => navigate('/meeting/create')}>
                                {t('meetingsList.createBtn')}
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <CardGrid>
                    {list.map(m => (
                        <MeetingCard
                            key={m.id}
                            name={m.title}
                            when={[m.date, m.time].filter(Boolean).join(' · ') || t('home.scheduleNotSet')}
                            where={m.hotelName || m.location || t('meeting.placeNotSet')}
                            time={m.dDay ? <TimeBadge size="sm" past={m.status === 'ended'}>{m.dDay}</TimeBadge> : undefined}
                            badges={badgesOf(m)}
                            people={<span className={styles.people}>{t('home.members', { count: m.memberCount })}</span>}
                            onClick={() => navigate(`/meeting/detail/${m.id}`)}
                        />
                    ))}
                </CardGrid>
            )}
        </AppScreen>
    );
};
